import React, { useEffect, useState, useMemo } from 'react';
import './CookieConsent.css';

const STORAGE_KEY = 'cookieConsent.v1';
const COOKIE_NAME = 'cookie_consent_v1';
const ONE_YEAR = 60 * 60 * 24 * 365;

const defaultPrefs = { necessary: true, analytics: false, marketing: false };

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...defaultPrefs, ...parsed };
  } catch {
    return null;
  }
}

function setConsentCookie(value) {
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(value))}; Max-Age=${ONE_YEAR}; Path=/; SameSite=Lax`;
}

export default function CookieConsent() {
  const stored = useMemo(() => readStored(), []);
  const [showBanner, setShowBanner] = useState(!stored);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(stored || defaultPrefs);

  useEffect(() => {
    // API global para reabrir el gestor desde cualquier parte (p.ej. footer)
    window.cookieConsent = {
      get: () => ({ ...prefs }),
      hasConsent: (cat) => !!prefs?.[cat],
      open: () => { setShowBanner(true); setShowPrefs(false); },
      openSettings: () => { setShowBanner(true); setShowPrefs(true); }
    };
  }, [prefs]);

  const save = (next) => {
    const final = { ...defaultPrefs, ...next };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(final));
    setConsentCookie(final);
    setPrefs(final);
    setShowBanner(false);
    setShowPrefs(false);
    window.dispatchEvent(new Event('cookieConsentUpdated'));
  };

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, analytics: false, marketing: false });
  const savePrefs = () => save(prefs);

  if (!showBanner) return null;

  return (
    <div className="cookie-root" role="dialog" aria-live="polite" aria-label="Aviso de cookies">
      {/* Banner */}
      <div className="cookie-banner">
        <div className="cookie-text">
          Uso cookies necesarias para el funcionamiento del sitio y, opcionalmente, cookies de análisis y marketing para mejorar tu experiencia.
          Puedes aceptar todas, rechazarlas o configurar tus preferencias. Consulta la
          <a className="cookie-link" href="/privacidad"> Política de Privacidad</a>.
        </div>
        <div className="cookie-actions">
          <button className="btn btn-secondary" onClick={() => setShowPrefs(true)}>Configurar</button>
          <button className="btn" onClick={rejectAll}>Rechazar</button>
          <button className="btn btn-primary" onClick={acceptAll}>Aceptar</button>
        </div>
      </div>

      {/* Modal de preferencias */}
      {showPrefs && (
        <div className="cookie-modal" role="dialog" aria-modal="true" aria-label="Preferencias de cookies">
          <div className="cookie-modal-card">
            <h3>Preferencias de cookies</h3>
            <p>Selecciona qué categorías de cookies opcionales aceptas. Las cookies necesarias siempre están activas.</p>

            <div className="cookie-toggle">
              <div>
                <strong>Necesarias</strong> (siempre activas)
                <p>Imprescindibles para el funcionamiento básico del sitio.</p>
              </div>
              <label className="switch">
                <input type="checkbox" checked disabled readOnly />
                <span className="slider" />
              </label>
            </div>

            <div className="cookie-toggle">
              <div>
                <strong>Análisis</strong>
                <p>Me ayuda a entender el uso de la web para mejorarla.</p>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                />
                <span className="slider" />
              </label>
            </div>

            <div className="cookie-toggle">
              <div>
                <strong>Marketing</strong>
                <p>Permiten personalizar publicidad y medir su eficacia.</p>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={prefs.marketing}
                  onChange={(e) => setPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                />
                <span className="slider" />
              </label>
            </div>

            <div className="cookie-modal-actions">
              <button className="btn" onClick={rejectAll}>Rechazar todo</button>
              <button className="btn btn-secondary" onClick={() => setShowPrefs(false)}>Volver</button>
              <button className="btn btn-primary" onClick={savePrefs}>Guardar preferencias</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 